import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCosts, type CostGroupBy } from '../../api/admin.js';
import { nanoToUsdCompact, formatNumber, nanoToUsd } from '../../lib/format.js';

const GROUP_OPTIONS: CostGroupBy[] = ['day', 'kind', 'provider', 'model', 'module', 'user'];

export function CostsPage(): ReactElement {
  const [groupBy, setGroupBy] = useState<CostGroupBy>('day');
  const [days, setDays] = useState(30);
  const from = useMemo(() => new Date(Date.now() - days * 86400_000).toISOString(), [days]);
  const to = useMemo(() => new Date().toISOString(), []);

  const query = useQuery({
    queryKey: ['admin', 'costs', { groupBy, days }],
    queryFn: () => fetchCosts({ from, to, groupBy }),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Costs</h1>
          <p className="page-sub">GROUP BY {groupBy} · últimos {days} días</p>
        </div>
      </div>
      <div className="filterbar">
        <div className="group">
          <span className="label" style={{ margin: 0 }}>
            Group by
          </span>
          <div className="chip-group">
            {GROUP_OPTIONS.map((g) => (
              <button
                key={g}
                type="button"
                className={`chip${groupBy === g ? ' is-on' : ''}`}
                onClick={() => setGroupBy(g)}
                data-testid={`costs-group-${g}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
        <label className="group">
          <span>días</span>
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
            style={{ minWidth: 80 }}
          />
        </label>
      </div>
      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <div className="pad-7">
          <section className="card" style={{ marginBottom: 24 }}>
            <header className="card-head">
              <h3 className="card-title">Stacked area</h3>
            </header>
            <StackedAreaChart data={query.data} />
          </section>
          <section className="card">
            <header className="card-head">
              <h3 className="card-title">Breakdown</h3>
            </header>
            <table className="table" data-testid="costs-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>{groupBy}</th>
                  <th className="r">Cost</th>
                  <th className="r">Calls</th>
                  <th className="r">Errors</th>
                </tr>
              </thead>
              <tbody>
                {query.data.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="muted" style={{ textAlign: 'center' }}>
                      Sin datos
                    </td>
                  </tr>
                ) : (
                  query.data.map((row, i) => (
                    <tr key={`${row.date}-${row.bucket ?? 'all'}-${i}`}>
                      <td className="mono">{row.date.slice(0, 10)}</td>
                      <td className="mono tt-sec">{row.bucket ?? '—'}</td>
                      <td className="r mono">{nanoToUsd(row.totalCostNano)}</td>
                      <td className="r mono">{formatNumber(row.callCount)}</td>
                      <td className="r mono">
                        {row.errorCount > 0 ? (
                          <span className="badge b-error">{row.errorCount}</span>
                        ) : (
                          <span className="muted">0</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </>
  );
}

interface CostRowLike {
  date: string;
  bucket: string | null;
  totalCostNano: string;
}

function StackedAreaChart({ data }: { data: ReadonlyArray<CostRowLike> }): ReactElement {
  // SVG simple sin recharts: aggregate por día, stack por bucket.
  const { buckets, totals } = useMemo(() => {
    const dateSet = new Set<string>();
    const bucketSet = new Set<string>();
    const matrix = new Map<string, Map<string, bigint>>();
    for (const row of data) {
      const day = row.date.slice(0, 10);
      const bucket = row.bucket ?? 'all';
      dateSet.add(day);
      bucketSet.add(bucket);
      const byBucket = matrix.get(day) ?? new Map<string, bigint>();
      byBucket.set(bucket, BigInt(row.totalCostNano));
      matrix.set(day, byBucket);
    }
    const dateArr = [...dateSet].sort();
    const bucketArr = [...bucketSet].sort();
    const totalsArr = dateArr.map((d) => {
      const byBucket = matrix.get(d) ?? new Map<string, bigint>();
      let total = 0n;
      const perBucket: bigint[] = bucketArr.map((b) => {
        const v = byBucket.get(b) ?? 0n;
        total += v;
        return v;
      });
      return { date: d, perBucket, total };
    });
    return { buckets: bucketArr, totals: totalsArr };
  }, [data]);

  if (totals.length === 0) {
    return <div className="empty">Sin datos para chart</div>;
  }

  const maxTotalBigInt = totals.reduce((m, t) => (t.total > m ? t.total : m), 0n);
  if (maxTotalBigInt === 0n) {
    return <div className="empty">Costo total = $0 en el rango</div>;
  }

  // Para el cálculo de y trabajamos con números (los costos diarios entran
  // cómodos en double; bigint sólo importa para totales acumulados).
  const maxTotal = Number(maxTotalBigInt);
  const width = 800;
  const height = 220;
  const padL = 40;
  const padB = 24;
  const innerW = width - padL - 16;
  const innerH = height - padB - 12;
  const stepX = totals.length > 1 ? innerW / (totals.length - 1) : 0;
  const palette: string[] = [
    'var(--accent)',
    'var(--k-llm)',
    'var(--k-tts)',
    'var(--k-stt)',
    'var(--k-tool_call)',
    'var(--k-module_step)',
    'var(--k-retrieval)',
    'var(--k-pipeline_stage)',
  ];

  function yFor(value: number): number {
    return innerH - (value / maxTotal) * innerH + 12;
  }

  const seriesPaths = buckets.map((_bucket, bi) => {
    const top: Array<[number, number]> = [];
    const bottom: Array<[number, number]> = [];
    for (let i = 0; i < totals.length; i += 1) {
      const t = totals[i];
      if (!t) continue;
      let lowerSum = 0;
      for (let j = 0; j < bi; j += 1) lowerSum += Number(t.perBucket[j] ?? 0n);
      const upperSum = lowerSum + Number(t.perBucket[bi] ?? 0n);
      const x = padL + stepX * i;
      top.push([x, yFor(upperSum)]);
      bottom.push([x, yFor(lowerSum)]);
    }
    return (
      'M' +
      top.map(([x, y]) => `${x},${y}`).join('L') +
      'L' +
      bottom.reverse().map(([x, y]) => `${x},${y}`).join('L') +
      'Z'
    );
  });

  return (
    <div className="chart-host">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" data-testid="costs-chart">
        {seriesPaths.map((d, i) => (
          <path key={buckets[i] ?? i} d={d} fill={palette[i % palette.length] ?? 'var(--accent)'} opacity={0.75} />
        ))}
        {totals.map((t, i) => (
          <text
            key={t.date}
            x={padL + stepX * i}
            y={height - 6}
            fontSize={9}
            fill="var(--text-tertiary)"
            textAnchor="middle"
          >
            {t.date.slice(5)}
          </text>
        ))}
        <text x={padL} y={12} fontSize={10} fill="var(--text-tertiary)">
          {nanoToUsdCompact(maxTotal.toString())}
        </text>
      </svg>
      <div className="chart-legend">
        {buckets.map((b, i) => (
          <span key={b}>
            <span className="chart-legend-swatch" style={{ background: palette[i % palette.length] ?? 'var(--accent)' }} />
            {b}
          </span>
        ))}
      </div>
    </div>
  );
}
